// Phase 9E — Faceted Archive search.
//
// v1: metadata-only search (Purpose / Audience / Year / Decision / ContentType
// + free-text on title / decisionRationale / producer name). Phase 10E will
// add full-text via the embedding pipeline.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { usePermissions } from '../../hooks/usePermissions';
import type { User } from './types';
import './ContentReview.css';

interface ArchiveSearchProps {
  /** Q3 (2026-08-11) — open the originating review cycle (full comments/
   *  mentions trail) from the archive detail. */
  onOpenCycle?: (cycleId: string) => void;
  user: User | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
}

interface FacetBucket { value: string | number; count: number }
interface Facets {
  purpose: FacetBucket[];
  audience: FacetBucket[];
  // v2 (D90) — the two new lineage dimensions (slug values).
  topic?: FacetBucket[];
  geography?: FacetBucket[];
  contentType: FacetBucket[];
  decision: FacetBucket[];
  year: FacetBucket[];
}

interface ReviewerOutcome {
  reviewer?: { _id?: string; name?: string; email?: string };
  riskLevel?: string | null;
  decision?: string | null;
  summary?: string;
}

interface ArchiveEntry {
  _id: string;
  reviewCycleId?: string;
  sharepointFileRef?: { driveId?: string; fileId?: string; path?: string };
  // 14e-B — pointer to the version-less working file in
  // `Approved content – working files/…`. source is one of:
  // 'companion' | 'primary' | 'link-only' | 'reason' | ''.
  workingFileRef?: { driveId?: string; fileId?: string; path?: string; webUrl?: string; source?: string };
  metadata: {
    purpose: string;
    audience: string;
    topic?: string | null;
    geography?: string | null;
    title: string;
    version: string;
    contentType: string;
    sourceUrl?: string;
    sourceEnv?: string | null;
    producer?: { teamMemberId?: string; name?: string; email?: string };
    submittedAt?: string;
    finalDecision: 'approved' | 'rejected' | 'superseded';
    decidedBy?: { teamMemberId?: string; name?: string };
    decidedAt: string;
    decisionRationale?: string;
    jurisdictionTags?: string[];
    supersededBy?: string | null;
  };
  reviewSummary?: {
    reviewerOutcomes?: ReviewerOutcome[];
    commentCount?: number;
    deadline?: string | null;
    deadlineMet?: boolean | null;
    stepCompletions?: { step: string; completedByName?: string | null; completedAt?: string | null }[];
    consultationCount?: number;
  };
  webUrl?: string | null;
  createdAt: string;
}

interface SelectedFilters {
  purpose: Set<string>;
  audience: Set<string>;
  topic: Set<string>;
  geography: Set<string>;
  contentType: Set<string>;
  decision: Set<string>;
  year: Set<string>;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const DECISION_LABELS: Record<string, string> = {
  approved: 'Approved',
  rejected: 'Rejected',
  superseded: 'Superseded'
};

// Format = the FILE family (auto-detected contentType), labelled by its technical
// name so no word ever appears on both the Type and Format axes (Fredrik
// 2026-09-08: "Presentation" was on both). Keep in step with ReviewDashboard.
const CONTENT_TYPE_LABELS: Record<string, string> = {
  docx: 'Word',
  pptx: 'PowerPoint',
  pdf: 'PDF',
  webpage: 'Web page',
  video: 'Video file',
  image: 'Image file'
};

function emptyFilters(): SelectedFilters {
  return {
    purpose: new Set(),
    audience: new Set(),
    topic: new Set(),
    geography: new Set(),
    contentType: new Set(),
    decision: new Set(),
    year: new Set()
  };
}

function filtersToParams(filters: SelectedFilters, q: string, includeSuperseded: boolean, liveOnly: boolean): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.purpose.size) params.set('purpose', Array.from(filters.purpose).join(','));
  if (filters.audience.size) params.set('audience', Array.from(filters.audience).join(','));
  if (filters.topic.size) params.set('topic', Array.from(filters.topic).join(','));
  if (filters.geography.size) params.set('geography', Array.from(filters.geography).join(','));
  if (filters.contentType.size) params.set('contentType', Array.from(filters.contentType).join(','));
  if (filters.decision.size) params.set('decision', Array.from(filters.decision).join(','));
  if (filters.year.size) params.set('year', Array.from(filters.year).join(','));
  if (q.trim()) params.set('q', q.trim());
  if (includeSuperseded) params.set('includeSuperseded', 'true');
  // Live materials (2026-09-02) — latest approved of each lineage. The
  // backend makes this win over decision/includeSuperseded; the UI keeps
  // the contradictory toggle disabled while it is on.
  if (liveOnly) params.set('live', 'true');
  return params;
}

const ArchiveSearch: React.FC<ArchiveSearchProps> = ({ user, getAuthHeaders, onOpenCycle }) => {
  const { hasSubcategoryAccess } = usePermissions(user);
  const canDelete = hasSubcategoryAccess('canManageArchive');

  const [facets, setFacets] = useState<Facets | null>(null);
  const [filters, setFilters] = useState<SelectedFilters>(emptyFilters);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [liveOnly, setLiveOnly] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ArchiveEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<ArchiveEntry | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  // Fetch facets once on mount (and when filters change so totalCount reflects).
  const fetchFacets = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const params = filtersToParams(filters, q, includeSuperseded, liveOnly);
      const res = await axios.get(
        `${backendUrl}/api/content-review/archive/facets?${params.toString()}`,
        { headers }
      );
      setFacets(res.data.facets);
      setTotalCount(res.data.totalCount || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load facets');
    }
  }, [filters, q, includeSuperseded, liveOnly, getAuthHeaders]);

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      const params = filtersToParams(filters, q, includeSuperseded, liveOnly);
      params.set('limit', '50');
      const res = await axios.get(
        `${backendUrl}/api/content-review/archive/search?${params.toString()}`,
        { headers }
      );
      setResults(res.data.results || []);
      setTotalCount(res.data.totalCount || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [filters, q, includeSuperseded, liveOnly, getAuthHeaders]);

  useEffect(() => { fetchFacets(); fetchResults(); }, [fetchFacets, fetchResults]);

  const toggleFilter = (group: keyof SelectedFilters, value: string) => {
    setFilters(prev => {
      const next: SelectedFilters = {
        purpose: new Set(prev.purpose),
        audience: new Set(prev.audience),
        topic: new Set(prev.topic),
        geography: new Set(prev.geography),
        contentType: new Set(prev.contentType),
        decision: new Set(prev.decision),
        year: new Set(prev.year)
      };
      if (next[group].has(value)) next[group].delete(value);
      else next[group].add(value);
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilters(emptyFilters());
    setQ('');
    setIncludeSuperseded(false);
    setLiveOnly(false);
  };

  const openDetail = useCallback(async (entry: ArchiveEntry) => {
    setSelected(entry);
    setShowDeleteForm(false);
    setDeleteReason('');
    setSelectedLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(
        `${backendUrl}/api/content-review/archive/${entry._id}`,
        { headers }
      );
      setSelected(res.data);
    } catch (err: any) {
      // Keep the lite entry on error; just no webUrl.
      console.warn('Failed to load full archive entry:', err.message);
    } finally {
      setSelectedLoading(false);
    }
  }, [getAuthHeaders]);

  const closeDetail = () => {
    setSelected(null);
    setShowDeleteForm(false);
    setDeleteReason('');
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (deleteReason.trim().length < 5) {
      setError('Reason must be at least 5 characters');
      return;
    }
    if (!window.confirm(
      `Permanently delete "${selected.metadata.title}" V${selected.metadata.version}? ` +
      `This will remove the SharePoint file and the audit record. The deletion is logged.`
    )) return;
    try {
      setDeleting(true);
      const headers = await getAuthHeaders();
      await axios.delete(
        `${backendUrl}/api/content-review/archive/${selected._id}`,
        { headers, data: { reason: deleteReason.trim() } }
      );
      closeDetail();
      fetchResults();
      fetchFacets();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const facetSection = (
    title: string,
    group: keyof SelectedFilters,
    buckets: FacetBucket[] | undefined,
    labelFn?: (v: string | number) => string
  ) => {
    if (!buckets || buckets.length === 0) return null;
    const visible = buckets.filter(b => b.value !== null && b.value !== '' && b.value !== undefined);
    if (visible.length === 0) return null;
    return (
      <div className="archive-facet-group">
        <div className="archive-facet-title">{title}</div>
        {visible.map(b => {
          const v = String(b.value);
          const checked = filters[group].has(v);
          const label = labelFn ? labelFn(b.value) : v;
          return (
            <label key={v} className="archive-facet-row">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleFilter(group, v)}
              />
              <span className="archive-facet-label">{label}</span>
              <span className="archive-facet-count">{b.count}</span>
            </label>
          );
        })}
      </div>
    );
  };

  const activeFilterCount = useMemo(() => (
    filters.purpose.size + filters.audience.size + filters.topic.size + filters.geography.size + filters.contentType.size +
    filters.decision.size + filters.year.size + (q.trim() ? 1 : 0) + (includeSuperseded ? 1 : 0) + (liveOnly ? 1 : 0)
  ), [filters, q, includeSuperseded, liveOnly]);

  return (
    <div className="archive-search">
      <div className="archive-search-header">
        <input
          type="text"
          className="archive-search-input"
          placeholder="Search title, decision rationale, producer…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <label className="archive-include-superseded archive-live-only" title="Only the latest approved version of each material — what is currently in circulation">
          <input
            type="checkbox"
            checked={liveOnly}
            onChange={e => setLiveOnly(e.target.checked)}
          />
          Live only
        </label>
        <label className="archive-include-superseded">
          <input
            type="checkbox"
            checked={liveOnly ? false : includeSuperseded}
            disabled={liveOnly}
            onChange={e => setIncludeSuperseded(e.target.checked)}
          />
          Include superseded
        </label>
        {activeFilterCount > 0 && (
          <button className="archive-clear-button" onClick={clearAllFilters}>
            Clear all ({activeFilterCount})
          </button>
        )}
        <div className="archive-result-count">
          {loading ? 'Searching…' : `${totalCount} entr${totalCount === 1 ? 'y' : 'ies'}`}
        </div>
      </div>

      {error && <div className="archive-error">{error}</div>}

      <div className="archive-search-body">
        <aside className="archive-facets">
          {facets ? (
            <>
              {facetSection('Decision', 'decision', facets.decision,
                v => DECISION_LABELS[String(v)] || String(v))}
              {/* D75 (2026-08-11): Purpose was renamed Type in the UI; the archive field name stays `purpose`. */}
              {facetSection('Type', 'purpose', facets.purpose)}
              {facetSection('Audience', 'audience', facets.audience)}
              {/* v2 (D90) — the new lineage dimensions are facetable. */}
              {facets.topic && facets.topic.length > 0 && facetSection('Topic', 'topic', facets.topic)}
              {facets.geography && facets.geography.length > 0 && facetSection('Geography', 'geography', facets.geography)}
              {facetSection('Format', 'contentType', facets.contentType,
                v => CONTENT_TYPE_LABELS[String(v)] || String(v))}
              {facetSection('Year', 'year', facets.year)}
            </>
          ) : (
            <div className="archive-facets-loading">Loading facets…</div>
          )}
        </aside>

        <div className="archive-results">
          {!loading && results.length === 0 && (
            <div className="archive-empty">No archive entries match these filters.</div>
          )}
          {results.map(entry => (
            <div
              key={entry._id}
              className={`archive-result-card archive-result-${entry.metadata.finalDecision}`}
              onClick={() => openDetail(entry)}
            >
              <div className="archive-result-row">
                <div className="archive-result-title">
                  {entry.metadata.title}
                  {entry.metadata.supersededBy && (
                    <span className="archive-superseded-badge" title="Superseded by a newer version">superseded</span>
                  )}
                  {entry.metadata.finalDecision === 'approved' && !entry.metadata.supersededBy && (
                    <span className="archive-live-badge" title="Latest approved version — currently in circulation">live</span>
                  )}
                </div>
                <div className={`archive-result-decision archive-decision-${entry.metadata.finalDecision}`}>
                  {DECISION_LABELS[entry.metadata.finalDecision]}
                </div>
              </div>
              <div className="archive-result-meta">
                <span>{entry.metadata.purpose}</span>
                <span>·</span>
                <span>{entry.metadata.audience}</span>
                <span>·</span>
                <span>V{entry.metadata.version}</span>
                <span>·</span>
                <span>{CONTENT_TYPE_LABELS[entry.metadata.contentType] || entry.metadata.contentType}</span>
                <span>·</span>
                <span>{new Date(entry.metadata.decidedAt).toLocaleDateString()}</span>
                {entry.metadata.producer?.name && (
                  <>
                    <span>·</span>
                    <span>by {entry.metadata.producer.name}</span>
                  </>
                )}
                {/* 14e-C — working-file indicator on archive rows. Green =
                    editable exists in the working-files tree; open the
                    detail modal for the path. */}
                {(entry.workingFileRef?.source === 'companion' || entry.workingFileRef?.source === 'primary') && entry.workingFileRef?.path && (
                  <>
                    <span>·</span>
                    <span style={{ color: '#1e6f2c' }} title={`Working file: ${entry.workingFileRef.path}`}>📎 editable</span>
                  </>
                )}
              </div>
              {entry.metadata.decisionRationale && (
                <div className="archive-result-rationale">{entry.metadata.decisionRationale}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {selected && (
        <div className="archive-detail-overlay" onClick={closeDetail}>
          <div className="archive-detail-modal" onClick={e => e.stopPropagation()}>
            <button className="archive-detail-close" onClick={closeDetail}>×</button>
            <h2>{selected.metadata.title}</h2>
            <div className={`archive-detail-decision archive-decision-${selected.metadata.finalDecision}`}>
              {DECISION_LABELS[selected.metadata.finalDecision]} · V{selected.metadata.version}
            </div>

            <dl className="archive-detail-grid">
              <dt>Type</dt><dd>{selected.metadata.purpose}</dd>
              {/* v2 — slugs shown raw here; the registry labels live one
                  fetch away and the slug IS the stable identity. */}
              <dt>Topic</dt><dd>{selected.metadata.topic || 'other (pre-topic entry)'}</dd>
              <dt>Geography</dt><dd>{selected.metadata.geography || 'global'}</dd>
              <dt>Audience</dt><dd>{selected.metadata.audience}</dd>
              <dt>Format</dt><dd>{CONTENT_TYPE_LABELS[selected.metadata.contentType] || selected.metadata.contentType}</dd>
              <dt>Decided</dt><dd>{new Date(selected.metadata.decidedAt).toLocaleString()}</dd>
              <dt>Decided by</dt><dd>{selected.metadata.decidedBy?.name || '—'}</dd>
              <dt>Producer</dt><dd>{selected.metadata.producer?.name || '—'}</dd>
              {selected.metadata.submittedAt && (
                <>
                  <dt>Submitted</dt>
                  <dd>{new Date(selected.metadata.submittedAt).toLocaleDateString()}</dd>
                </>
              )}
              {selected.metadata.sourceUrl && (
                <>
                  <dt>Source URL</dt>
                  <dd><a href={selected.metadata.sourceUrl} target="_blank" rel="noopener noreferrer">{selected.metadata.sourceUrl}</a></dd>
                </>
              )}
              {selected.metadata.jurisdictionTags && selected.metadata.jurisdictionTags.length > 0 && (
                <>
                  <dt>Jurisdiction</dt>
                  <dd>{selected.metadata.jurisdictionTags.join(', ')}</dd>
                </>
              )}
              {selected.metadata.supersededBy && (
                <>
                  <dt>Superseded by</dt>
                  <dd>Newer entry exists in this lineage</dd>
                </>
              )}
              {/* WS4 — where the channel handler put the production copy. */}
              {(selected as any).implementedRefs?.channel && (
                <>
                  <dt>Distributed</dt>
                  <dd>
                    {(selected as any).implementedRefs.channel}
                    {(selected as any).implementedRefs.dryRun && ' (dry-run)'}
                    {(selected as any).implementedRefs.manual && ' (manual — ops notified)'}
                  </dd>
                </>
              )}
              {(selected as any).implementedRefs?.hubspotFileUrl && (
                <>
                  <dt>HubSpot file</dt>
                  <dd>
                    <a href={(selected as any).implementedRefs.hubspotFileUrl} target="_blank" rel="noopener noreferrer">
                      {(selected as any).implementedRefs.hubspotFolderPath || 'Open in Content Hub'}
                    </a>
                  </dd>
                </>
              )}
              {/* 2026-08-24 — this used to read "Published page" and render
                  cycle.sourceUrl, so a sandbox cycle showed the sandbox URL
                  under a label claiming it was live. Three states now:
                  published (a real live link), pending (the destination the
                  page is headed for, which a human still has to deploy), and
                  unresolved (no domain is mapped for the Site/Purpose — say
                  so rather than link somewhere invented). Legacy entries
                  carry no `published` flag; `manual` distinguishes them. */}
              {(selected as any).implementedRefs?.pageUrl && (
                <>
                  <dt>
                    {(selected as any).implementedRefs.published === true
                      || ((selected as any).implementedRefs.published === undefined
                          && !(selected as any).implementedRefs.manual)
                      ? 'Published page'
                      : 'Publishes to'}
                  </dt>
                  <dd>
                    <a href={(selected as any).implementedRefs.pageUrl} target="_blank" rel="noopener noreferrer">
                      {(selected as any).implementedRefs.pageUrl}
                    </a>
                    {(selected as any).implementedRefs.published === false && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        Not published by cellcohub — deployed manually from HubSpot.
                      </div>
                    )}
                  </dd>
                </>
              )}
              {!(selected as any).implementedRefs?.pageUrl
                && (selected as any).implementedRefs?.pageUrlReason && (
                <>
                  <dt>Publishes to</dt>
                  <dd>
                    <span style={{ color: '#888' }}>
                      Unresolved — {(selected as any).implementedRefs.pageUrlReason}
                    </span>
                  </dd>
                </>
              )}
              {(selected as any).implementedRefs?.reviewedUrl
                && (selected as any).implementedRefs.reviewedUrl !== (selected as any).implementedRefs.pageUrl && (
                <>
                  <dt>Reviewed at</dt>
                  <dd>
                    {/* CRP-202605 (2026-09-08): the old /Portal files copies were deleted
                        and their hubspotusercontent URLs cannot be redirected — a link
                        would 404. Show the retired URL as text with the date instead. */}
                    {(selected as any).implementedRefs.previousPortalRetiredAt
                      && (selected as any).implementedRefs.reviewedUrl === (selected as any).implementedRefs.previousPortalUrl ? (
                        <>
                          <span style={{ color: '#888', wordBreak: 'break-all' }}>
                            {(selected as any).implementedRefs.reviewedUrl}
                          </span>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            Retired {new Date((selected as any).implementedRefs.previousPortalRetiredAt).toLocaleDateString()} — the file now publishes from the Content Hub; this old link no longer resolves.
                          </div>
                        </>
                      ) : (
                        <a href={(selected as any).implementedRefs.reviewedUrl} target="_blank" rel="noopener noreferrer">
                          {(selected as any).implementedRefs.reviewedUrl}
                        </a>
                      )}
                  </dd>
                </>
              )}
              {/* 14e-B — editable working file. Four rendering modes per the
                  D81 matrix; only one applies per cycle. */}
              {selected.workingFileRef?.source === 'companion' && selected.workingFileRef?.path && (
                <>
                  <dt>Editable source</dt>
                  <dd>
                    <span title="Companion file (docx/pptx/xlsx) captured at submission — D81">
                      {selected.workingFileRef.webUrl ? (
                        <a href={selected.workingFileRef.webUrl} target="_blank" rel="noopener noreferrer">{selected.workingFileRef.path}</a>
                      ) : selected.workingFileRef.path}
                    </span>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      Working file (version-less, per lineage).
                    </div>
                  </dd>
                </>
              )}
              {selected.workingFileRef?.source === 'primary' && selected.workingFileRef?.path && (
                <>
                  <dt>Editable source</dt>
                  <dd>
                    <span title="Natively-editable primary file used as the working source">
                      {selected.workingFileRef.webUrl ? (
                        <a href={selected.workingFileRef.webUrl} target="_blank" rel="noopener noreferrer">{selected.workingFileRef.path}</a>
                      ) : selected.workingFileRef.path}
                    </span>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      Working file (version-less, per lineage).
                    </div>
                  </dd>
                </>
              )}
              {selected.workingFileRef?.source === 'link-only' && (
                <>
                  <dt>Editable source</dt>
                  <dd>
                    {(selected as any).metadata?.sourceLink ? (
                      <a href={(selected as any).metadata.sourceLink} target="_blank" rel="noopener noreferrer">
                        {(selected as any).metadata.sourceLink}
                      </a>
                    ) : (
                      <span style={{ color: '#888' }}>(link recorded on cycle)</span>
                    )}
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      Link-only — no working-file copy in SharePoint (D85/D86).
                    </div>
                  </dd>
                </>
              )}
              {selected.workingFileRef?.source === 'reason' && (
                <>
                  <dt>Editable source</dt>
                  <dd>
                    <span style={{ color: '#a15c00' }}>
                      No editable source
                      {(selected as any).metadata?.noEditableSourceReason
                        ? ` — ${(selected as any).metadata.noEditableSourceReason}`
                        : ''}
                    </span>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                      D81 exception — audited at submission.
                    </div>
                  </dd>
                </>
              )}
            </dl>

            {selected.metadata.decisionRationale && (
              <div className="archive-detail-section">
                <div className="archive-detail-section-title">Decision rationale</div>
                <div className="archive-detail-rationale">{selected.metadata.decisionRationale}</div>
              </div>
            )}

            {selected.reviewSummary?.reviewerOutcomes && selected.reviewSummary.reviewerOutcomes.length > 0 && (
              <div className="archive-detail-section">
                <div className="archive-detail-section-title">
                  Reviewer outcomes ({selected.reviewSummary.commentCount || 0} comments)
                </div>
                {selected.reviewSummary.reviewerOutcomes.map((o, i) => (
                  <div key={i} className="archive-reviewer-outcome">
                    <strong>{o.reviewer?.name || 'Unknown'}</strong>
                    {o.riskLevel && <span className={`archive-risk-tag risk-${o.riskLevel}`}>{o.riskLevel}</span>}
                    {o.summary && <div className="archive-reviewer-summary">{o.summary}</div>}
                  </div>
                ))}
              </div>
            )}

            {selected.reviewSummary?.stepCompletions && selected.reviewSummary.stepCompletions.length > 0 && (
              <div className="archive-detail-section">
                <div className="archive-detail-section-title">
                  Review trail
                  {(selected.reviewSummary.consultationCount || 0) > 0 &&
                    ` (${selected.reviewSummary.consultationCount} consultation${selected.reviewSummary.consultationCount === 1 ? '' : 's'})`}
                </div>
                {selected.reviewSummary.stepCompletions.map((sc, i) => (
                  <div key={i} className="archive-reviewer-outcome">
                    <strong style={{ textTransform: 'capitalize' }}>{sc.step}</strong>
                    {' — '}{sc.completedByName || 'Unknown'}
                    {sc.completedAt && <span style={{ color: '#868e96' }}> · {new Date(sc.completedAt).toLocaleString()}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="archive-detail-actions">
              {selectedLoading && <span className="archive-loading-inline">Loading file link…</span>}
              {selected.webUrl && (
                <a
                  href={selected.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="archive-button archive-button-primary"
                >
                  Open in SharePoint
                </a>
              )}
              {selected.reviewCycleId && onOpenCycle && (
                <button
                  className="archive-button"
                  onClick={() => onOpenCycle(selected.reviewCycleId!)}
                  title="Open the originating review cycle — full comments, mentions and consultation threads live there"
                >
                  Open review cycle
                </button>
              )}
              {selected.reviewCycleId && !onOpenCycle && (
                <span className="archive-cycle-ref">
                  Cycle: <code>{selected.reviewCycleId}</code>
                </span>
              )}
              {canDelete && (
                <button
                  className="archive-button archive-button-danger"
                  onClick={() => setShowDeleteForm(s => !s)}
                >
                  {showDeleteForm ? 'Cancel delete' : 'Delete (admin)'}
                </button>
              )}
            </div>

            {showDeleteForm && canDelete && (
              <div className="archive-delete-form">
                <label>
                  Reason for deletion (audit-logged, min 5 chars):
                  <textarea
                    value={deleteReason}
                    onChange={e => setDeleteReason(e.target.value)}
                    rows={3}
                    placeholder="e.g. GDPR right-to-erasure request from patient X"
                  />
                </label>
                <button
                  className="archive-button archive-button-danger"
                  disabled={deleting || deleteReason.trim().length < 5}
                  onClick={handleDelete}
                >
                  {deleting ? 'Deleting…' : 'Permanently delete'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchiveSearch;
